import type { PointOptionsObject } from "highcharts";
import HTTP from "owp.http";
import type { JSX } from "preact";
import { Suspense } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { BasePath } from "@gss/common/BasePath";
import { classNames } from "@gss/common/classNames";
import { useMessages } from "@gss/components/Messages";
import { getHighchartsConfig } from "../resources";
import "./Chart.css";
import { round1 } from "../util/formatters";
import { useTranslation } from "../util/Translation";
import { HighchartsReactLazy } from "./HighchartsReactLazy";
import * as util from "./HighchartsUtil";

const CHART_FONT_SIZE = 18;

interface Serie {
    filterSerie: {
        name: string;
        color: string;
    };
}

export type ChartVariant =
    | "spider"
    | "bar"
    | "column"
    | "dynamicColumn"
    | "superGraph";

export interface ChartData {
    data: (number | PointOptionsObject | null)[];
    confidenceInterval?: number;
    confidenceIntervals?: Record<string | number, number>;
}

interface Props<T> {
    variant: ChartVariant;
    className?: string;
    height?: number;
    title?: string;
    subtitle?: string;
    categories?: string[];
    series?: T[];
    callback?: (serie: T) => ChartData;
    suffix?: string;
    xTitle?: string;
    yTitle: string | null;
    rotateDataLabels?: boolean;
    xFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction;
    yFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction;
    integerTooltip?: boolean;
    filename?: string;
    events?: Highcharts.SeriesEventsOptionsObject;
}

export function Chart<T extends Serie>({
    variant,
    className,
    height,
    title,
    subtitle,
    categories,
    series,
    callback,
    suffix,
    xTitle,
    yTitle,
    rotateDataLabels,
    xFormatter,
    yFormatter,
    integerTooltip,
    filename,
    events,
}: Props<T>): JSX.Element {
    const [options, setOptions] = useState<Highcharts.Options>();
    const { translation } = useTranslation();
    const { addError } = useMessages();

    useEffect(() => {
        if (series == null) {
            return;
        }

        const updateOptions = (options: Highcharts.Options) => {
            if (height != null) {
                util.setHeight(options, height);
            }
            if (title != null) {
                util.setTitle(options, title);
            }
            if (subtitle != null) {
                util.setSubtitle(options, subtitle);
            }
            if (xTitle != null) {
                util.setXTitle(options, xTitle);
            }
            if (yTitle != null) {
                util.setYTitle(options, yTitle);
            }
            if (categories != null) {
                util.setCategories(options, categories);
            }
            if (rotateDataLabels != null) {
                util.rotateDataLabels(options, rotateDataLabels);
            }
            if (xFormatter != null) {
                util.setXFormatter(options, xFormatter);
            }
            if (yFormatter != null) {
                util.setYFormatter(options, yFormatter);
            }
            if (events != null) {
                util.setEvents(options, events);
            }

            options.credits = { enabled: false };

            options.exporting = {
                ...options.exporting,
                ...getExporting(filename),
            };

            options.tooltip = getTooltip(
                suffix ?? "",
                categories != null,
                integerTooltip ?? false,
            );

            const optionsSeries = series.map((s) => ({
                ...s,
                name: s.filterSerie.name,
                color: s.filterSerie.color,
                ...(callback ? callback(s) : {}),
            }));

            options.series = optionsSeries;

            util.setChartFontSize(options, CHART_FONT_SIZE);

            setOptions(options);
        };

        if (options != null) {
            updateOptions({});
        } else {
            const optionsUrl = getHighchartsConfig(variant);

            HTTP.get(optionsUrl)
                .then((r) => r.json<Highcharts.Options>())
                .then((fetchedOptions) => {
                    // Clone options to avoid mutating the original
                    const options = structuredClone(fetchedOptions);

                    util.setAnimation(options, false);
                    if (categories == null) {
                        util.disableXLabels(options);
                    }
                    if (suffix != null) {
                        util.addLabelSuffix(options, suffix);
                    }

                    // The rotate so first category is at the top.
                    if (variant === "spider" && categories != null) {
                        const angle = 180 / categories.length;
                        util.setStartAngle(options, -angle);
                        // Important that the y-axis labels match up with the categories or they will be misaligned
                        util.setYAngle(options, angle);
                    }

                    updateOptions(options);
                })
                .catch((error: unknown) => {
                    addError({
                        message: translation.error.loadChart,
                        cause: error,
                    });
                });
        }
    }, [series, filename, title, yTitle, categories, events, rotateDataLabels]);

    const containerClassName = classNames(getVariantClass(variant), className);

    // Allocate size for chart
    if (options == null) {
        return <div className={containerClassName} />;
    }

    return (
        <Suspense fallback={null}>
            <HighchartsReactLazy
                containerProps={{ className: containerClassName }}
                options={{
                    ...options,
                    lang: translation.highcharts,
                    palette: {
                        ...options.palette,
                        colorScheme: "light",
                    },
                }}
            />
        </Suspense>
    );
}

function getVariantClass(variant: ChartVariant): string {
    switch (variant) {
        case "spider":
            return "chart-spider";
        case "bar":
            return "chart-bar";
        case "column":
            return "chart-column";
        case "dynamicColumn":
            return "chart-dynamic-column";
        case "superGraph":
            return "chart-super-graph";
        default: {
            const _ensureExhaustive: never = variant;
            throw new Error("Unknown Chart variant");
        }
    }
}

function getTooltip(
    suffix: string,
    hasCategories: boolean,
    isInteger: boolean,
): Highcharts.TooltipOptions {
    return {
        shared: true,
        useHTML: true,
        shadow: false,
        padding: 1.5,
        borderWidth: 1,
        borderColor: "black",
        formatter(): string {
            const header = hasCategories ? `${this.category}<br />` : "";
            const unformattedPoints = this.points ?? [this];
            const points = unformattedPoints
                .map((p) => pointFormatter(p, suffix, isInteger))
                .join("");
            return `${header}<table>${points}</table>`;
        },
    };
}

function pointFormatter(
    p: Highcharts.Point,
    suffix: string,
    isInteger: boolean,
): string {
    const value = isInteger ? (p.y ?? 0) : round1(p.y ?? 0);
    const color = typeof p.color === "string" ? p.color : String(p.color);
    return `<tr><td><span style="color:${color}">●</span>${p.series.name}</td><td><b>${value}${suffix}</b></td>${getConfidenceInterval(p, suffix)}</tr>`;
}

function getConfidenceInterval(p: Highcharts.Point, suffix: string): string {
    const { options } = p.series;
    if (!isCiOptions(options)) {
        return "";
    }
    const ci =
        options.confidenceInterval ?? options.confidenceIntervals?.[p.category];
    if (ci == null) {
        return "";
    }
    return `<td>(± ${round1(ci)}${suffix})</td>`;
}

function isCiOptions(
    options: Highcharts.SeriesOptionsType,
): options is Highcharts.SeriesOptionsType & {
    confidenceInterval?: number;
    confidenceIntervals?: Record<string | number, number>;
} {
    return "confidenceInterval" in options || "confidenceIntervals" in options;
}

function getExporting(filename?: string): Highcharts.ExportingOptions {
    return {
        url: new URL(`${BasePath.rest()}/export/highcharts`),
        // FIXME: Allow html in exported chart
        allowHTML: false,
        filename,
        sourceWidth: 1000,
        scale: 1,
        // First use local resource and fallback to export server
        local: true,
        fallbackToExportServer: true,
        buttons: {
            contextButton: {
                menuItems: [
                    "viewFullscreen",
                    "printChart",
                    "separator",
                    "downloadPNG",
                    "downloadJPEG",
                    "downloadPDF",
                    "downloadSVG",
                    "separator",
                    "downloadCSV",
                    "downloadXLS",
                ],
            },
        },
    };
}
